import { Grid, TextField } from "@mui/material";
import { useState } from "react";
import * as React from "react";
import { Form, Formik, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import Notification from "../popup/Notification";
import ConfirmDialog from "../dialog/ConfirmDialog";
import Stack from "@mui/joy/Stack";
import Button from "@mui/joy/Button";
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import { LightTooltip } from "../tooltip/ToolTip";

const AddSupplier = (props) => {
  const axiosPrivate = useAxiosPrivate();
  const { closeDialog, editSupplier, selectedSupplier } = props;

  var supplierInfo = null;
  var supplierID = 0;
  var name = null;
  var country = null;
  var city = null;
  var phone = null;
  var email = null;

  if (editSupplier) {
    supplierInfo = JSON.parse(selectedSupplier);
    supplierID = supplierInfo.id;
    name = supplierInfo.name;
    country = supplierInfo.country;
    city = supplierInfo.city;
    phone = supplierInfo.phone;
    email = supplierInfo.email;
  }

  const resetForm = (props) => {
    // clear input fields
    props.resetForm();
  };

  const closeForm = (message) => {
    closeDialog(message);
  };

  const initialValues = editSupplier
    ? {
        name: name,
        country: country,
        city: city,
        phone: phone,
        email: email,
      }
    : {
        name: "",
        country: "",
        city: "",
        phone: "",
        email: "",
      };

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .required("Required")
      .min(2, "Name too short")
      .max(40, "Name too long"),
    country: Yup.string()
      .required("Required")
      .min(2, "Country too short")
      .max(30, "Country too long"),
    city: Yup.string()
      .required("Required")
      .min(2, "City too short")
      .max(30, "City too long"),
    phone: Yup.string()
      .required("Required")
      .min(3, "Phone number too short")
      .max(20, "Phone number too long"),
    email: Yup.string().email("Please enter valid email").required("Required"),
  });

  const SUPPLIERS_URL = "/api/v1/suppliers/";
  const onSubmit = async (values, props) => {
    props.setSubmitting(true);

    // Common
    const data = {
      name: values.name,
      country: values.country,
      city: values.city,
      phone: values.phone,
      email: values.email,
    };

    try {
      const response = editSupplier
        ? await axiosPrivate.put(SUPPLIERS_URL + supplierID, data, {
            headers: {
              "Content-Type": "application/json",
            },
          })
        : await axiosPrivate.post(SUPPLIERS_URL + "save", data, {
            headers: {
              "Content-Type": "application/json",
            },
          });

      props.setSubmitting(false);
      resetForm(props);
      closeForm(response.data.message);
    } catch (error) {
      var errorMessage = "";
      if (error.response) {
        // The client was given an error response (5xx, 4xx)
        const errorData = error.response.data;
        errorMessage = errorData.message;
      } else if (error.request) {
        // The client never received a response, and the request was never left
        errorMessage = "No server response";
      } else {
        // Anything else
        errorMessage = "Unexpected error occurred";
      }
      const errorTitle = editSupplier
        ? "Failed To Update Supplier"
        : "Failed To Create Supplier";
      setConfirmDialog({
        isOpen: true,
        title: errorTitle,
        subTitle: errorMessage,
        action: "Retry",
        onConfirm: { onSubmit },
      });
    }
  };

  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
  });
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
  });

  return (
    <Grid>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
      >
        {(props) => (
          <Form>
            <Stack spacing={2} mt={3} direction="column">
              <Field
                as={TextField}
                label="Name"
                name="name"
                placeholder="Enter supplier name"
                fullWidth
                required
                helperText={
                  <ErrorMessage name="name">
                    {(msg) => <div style={{ color: "red" }}>{msg}</div>}
                  </ErrorMessage>
                }
              />
              <Field
                as={TextField}
                label="Country"
                name="country"
                placeholder="Enter country"
                fullWidth
                required
                helperText={
                  <ErrorMessage name="country">
                    {(msg) => <div style={{ color: "red" }}>{msg}</div>}
                  </ErrorMessage>
                }
              />
              <Field
                as={TextField}
                label="City"
                name="city"
                placeholder="Enter city"
                fullWidth
                required
                helperText={
                  <ErrorMessage name="city">
                    {(msg) => <div style={{ color: "red" }}>{msg}</div>}
                  </ErrorMessage>
                }
              />
              <Field
                as={TextField}
                label="Phone"
                name="phone"
                placeholder="Enter supplier phone"
                fullWidth
                required
                helperText={
                  <ErrorMessage name="phone">
                    {(msg) => <div style={{ color: "red" }}>{msg}</div>}
                  </ErrorMessage>
                }
              />
              <Field
                as={TextField}
                label="Email"
                name="email"
                placeholder="Enter supplier email"
                fullWidth
                required
                helperText={
                  <ErrorMessage name="email">
                    {(msg) => <div style={{ color: "red" }}>{msg}</div>}
                  </ErrorMessage>
                }
              />
            </Stack>

            <Stack spacing={2} mt={3} direction="row" justifyContent="flex-end">
              <LightTooltip title="Clear form">
                <Button
                  variant="contained"
                  sx={{
                    borderRadius: 8,
                    width: 120,
                    backgroundColor: "#808080",
                    padding: "7px 14px",
                    fontSize: "14px",
                    color: "white",
                    ":hover": {
                      bgcolor: "#696969",
                      color: "white",
                    },
                  }}
                  onClick={() => {
                    resetForm(props);
                  }}
                >
                  Reset
                </Button>
              </LightTooltip>

              <LightTooltip title="Submit details">
                <Button
                  loading={props.isSubmitting}
                  loadingPosition="start"
                  disabled={props.isSubmitting}
                  type="submit"
                  sx={{
                    width: 120,
                  }}
                  variant="solid"
                >
                  {props.isSubmitting ? "Submitting" : "Submit"}
                </Button>
              </LightTooltip>
            </Stack>
          </Form>
        )}
      </Formik>

      <Notification notify={notify} setNotify={setNotify} />
      <ConfirmDialog
        confirmDialog={confirmDialog}
        setConfirmDialog={setConfirmDialog}
      />
    </Grid>
  );
};

export default AddSupplier;
